import * as React from 'react'
import { CalendarDays, Database, Tags } from 'lucide-react'
import {
  useGetPssJournalCoverageQuery,
  useListPssMirrorsQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { CopyButton } from '../../../components/Chat/CopyButton'
import { Button } from '../../../components/ui/button'
import { formatCompactLocalTime, formatEventWindowUTC } from '../../../lib/time'
import { cn } from '../../../lib/utils'
import { WizardChrome } from './WizardChrome'
import type { IngestWizardApi } from './useIngestWizard'

interface StepUpdatePssProps {
  wizard: IngestWizardApi
}

export function timestampInputToUtcIso(value: string): string {
  if (!value.trim()) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString()
}

export function instantToDatetimeLocal(value: string): string {
  const d = new Date(value.trim())
  if (Number.isNaN(d.getTime())) return ''
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 23)
}

export function TimestampInput({
  id,
  label,
  value,
  onChange,
  invalid = false
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  invalid?: boolean
}) {
  const pickerRef = React.useRef<HTMLInputElement>(null)
  const [emptyPickerSeed] = React.useState(() =>
    instantToDatetimeLocal(new Date(Date.now() - 2 * 60_000).toISOString())
  )
  const openPicker = () => {
    const picker = pickerRef.current
    if (!picker) return
    if (typeof picker.showPicker === 'function') picker.showPicker()
    else picker.click()
  }

  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <label htmlFor={id} className="font-medium text-ink">
        {label}
      </label>
      <div className="flex min-w-0">
        <input
          id={id}
          type="text"
          placeholder="Paste ISO timestamp or enter local date/time"
          className={cn(
            'min-w-0 flex-1 rounded-l-md border border-r-0 bg-bg-elev px-3 py-2 font-mono text-sm',
            invalid ? 'border-st-down' : 'border-line'
          )}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={invalid || undefined}
        />
        <input
          ref={pickerRef}
          type="datetime-local"
          step="0.001"
          value={instantToDatetimeLocal(value) || emptyPickerSeed}
          onChange={(event) => onChange(event.target.value)}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={openPicker}
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-r-md border bg-bg-elev px-3 text-ink-2 hover:bg-bg-sunk',
            invalid ? 'border-st-down' : 'border-line'
          )}
          aria-label={`Open ${label.toLowerCase()} date and time picker`}
          title="Choose date and time"
        >
          <CalendarDays className="size-4" />
        </button>
      </div>
    </div>
  )
}

function CoverageBoundary({ label, timestamp }: { label: string; timestamp?: string }) {
  return (
    <div className="min-w-0 rounded-md border border-line/60 bg-bg-elev px-3 py-2.5">
      <div className="text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">{label}</div>
      {timestamp ? (
        <>
          <div className="mt-1 flex items-center gap-1">
            <code className="min-w-0 flex-1 truncate text-xs text-ink" title={timestamp}>
              {timestamp}
            </code>
            <CopyButton
              text={timestamp}
              label={`Copy ${label.toLowerCase()}`}
              className="shrink-0"
            />
          </div>
          <div className="mt-0.5 truncate text-11 text-ink-3">
            {formatCompactLocalTime(timestamp, { fallback: timestamp })}
          </div>
        </>
      ) : (
        <div className="mt-1 text-xs text-ink-3">No journal timestamp found</div>
      )}
    </div>
  )
}

export function StepUpdatePss({ wizard }: StepUpdatePssProps) {
  const { siteName, startPssUpdate, backToPick } = wizard
  const { data: mirrors, isFetching, isError, refetch } = useListPssMirrorsQuery()

  const [mirrorId, setMirrorId] = React.useState('')
  const [windowStartLocal, setWindowStartLocal] = React.useState('')
  const [windowEndLocal, setWindowEndLocal] = React.useState('')

  const selectedMirror = mirrors?.find((m) => m.id === mirrorId)
  const { data: coverage, isFetching: coverageLoading } = useGetPssJournalCoverageQuery(
    { mirrorId },
    { skip: !mirrorId }
  )

  const windowStart = timestampInputToUtcIso(windowStartLocal)
  const windowEnd = timestampInputToUtcIso(windowEndLocal)
  const rangeValid =
    windowStart &&
    windowEnd &&
    Date.parse(windowStart) < Date.parse(windowEnd) &&
    Date.parse(windowEnd) < Date.now() - 60_000

  const onContinue = () => {
    if (!mirrorId || !rangeValid || !selectedMirror) return
    startPssUpdate({
      action: 'backfill',
      mirrorId,
      connectionName: selectedMirror.name,
      windowStart,
      windowEnd
    })
  }

  const onSyncTags = () => {
    if (!mirrorId || !selectedMirror) return
    startPssUpdate({ action: 'sync_tags', mirrorId, connectionName: selectedMirror.name })
  }

  return (
    <WizardChrome
      eyebrow="Step 03 — Update PSS"
      title={`Update PlantState Suite data for ${siteName || 'this site'}`}
      description="Choose an existing PlantState Suite mirror, then either re-sync its tags and event journals onto the graph or pull an earlier journal time range. New events merge with data already in the mirror; scheduled refresh keeps its forward cursor."
      onBack={backToPick}
      onContinue={onContinue}
      continueLabel="Start journal backfill"
      continueDisabled={!mirrorId || !rangeValid}
    >
      {isFetching ? (
        <p className="py-6 text-center text-sm text-ink-3">Loading PSS mirrors…</p>
      ) : isError ? (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-st-down-bg bg-st-down-bg/30 p-3 text-sm text-st-down">
          <span>Failed to load mirrors.</span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-xs font-medium underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      ) : (mirrors?.length ?? 0) === 0 ? (
        <p className="py-6 text-center text-sm text-ink-3">
          No PlantState Suite mirrors exist for this organization yet. Run Add ingest first.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink">PSS connection</span>
            <select
              className="rounded-md border border-line bg-bg-elev px-3 py-2 text-sm"
              value={mirrorId}
              onChange={(e) => setMirrorId(e.target.value)}
            >
              <option value="">Select a mirror…</option>
              {(mirrors ?? []).map((mirror) => (
                <option key={mirror.id} value={mirror.id}>
                  {mirror.name}
                </option>
              ))}
            </select>
          </label>

          {mirrorId ? (
            <div className="rounded-lg border border-line bg-bg-sunk/30 p-3 text-sm">
              <div className="mb-2 flex items-center gap-2 font-medium text-ink">
                <Database className="size-4 text-ink-3" />
                Ingested journal coverage
              </div>
              {coverageLoading ? (
                <p className="text-ink-3">Loading coverage…</p>
              ) : coverage ? (
                <div className="flex flex-col gap-2 text-xs text-ink-2">
                  <p>
                    Mirror lookback setting:{' '}
                    <span className="font-mono">{coverage.lookback_hours}h</span>
                    {coverage.refresh_forward_cursor ? (
                      <>
                        {' '}
                        · forward refresh from{' '}
                        <span className="font-mono">
                          {formatCompactLocalTime(coverage.refresh_forward_cursor, {
                            fallback: coverage.refresh_forward_cursor
                          })}
                        </span>
                      </>
                    ) : null}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <CoverageBoundary label="Start boundary" timestamp={coverage.ingested_from} />
                    <CoverageBoundary label="End boundary" timestamp={coverage.ingested_through} />
                  </div>
                  {coverage.owners.length > 0 ? (
                    <details>
                      <summary className="cursor-pointer text-ink-3 hover:text-ink-2">
                        Per-owner ingest windows
                      </summary>
                      <ul className="mt-1 max-h-40 overflow-y-auto rounded border border-line/60 bg-bg-elev">
                        {coverage.owners.map((owner) => (
                          <li
                            key={owner.owner_id || owner.owner_name || 'unknown'}
                            className="border-b border-line/40 px-2 py-1.5 last:border-0"
                          >
                            <span className="font-medium text-ink">
                              {owner.owner_name || owner.owner_id || 'Owner'}
                            </span>
                            <span className="mt-0.5 block text-ink-3">
                              Last ingest window:{' '}
                              {formatEventWindowUTC(owner.last_ingest_from, owner.last_ingest_to) ||
                                '—'}
                              {owner.watermark ? (
                                <>
                                  {' '}
                                  · watermark{' '}
                                  {formatCompactLocalTime(owner.watermark, {
                                    fallback: owner.watermark
                                  })}
                                </>
                              ) : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              ) : (
                <p className="text-ink-3">No coverage details yet.</p>
              )}
            </div>
          ) : null}

          {mirrorId ? (
            <div className="flex flex-col gap-2 rounded-lg border border-line bg-bg-sunk/30 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-medium text-ink">
                  <Tags className="size-4 text-ink-3" />
                  PSS tags on the graph
                </div>
                <p className="mt-1 text-xs text-ink-2">
                  Re-project this mirror&apos;s tags and event journals as PssTag and
                  PssEventJournal nodes on a reviewable ingest branch. Run it after the mirror
                  picked up new tags or journals. No journal history is pulled.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={onSyncTags}
                disabled={!selectedMirror}
              >
                Re-sync PSS tags
              </Button>
            </div>
          ) : null}

          <div className="text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">
            Journal backfill
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TimestampInput
              id="pss-window-start"
              label="Pull from"
              value={windowStartLocal}
              onChange={setWindowStartLocal}
            />
            <TimestampInput
              id="pss-window-end"
              label="Pull through"
              value={windowEndLocal}
              onChange={setWindowEndLocal}
              invalid={Boolean(windowEndLocal && !rangeValid)}
            />
          </div>
          <p className="text-11 text-ink-3">
            Paste an ISO timestamp to preserve its exact UTC instant, or use the calendar buttons to
            choose local date and time.
          </p>
          {windowStartLocal && windowEndLocal && !rangeValid ? (
            <p className="text-xs text-st-down">
              Choose a valid range: start before end, and end at least one minute before now.
            </p>
          ) : null}
        </div>
      )}
    </WizardChrome>
  )
}
